import React, { useState } from "react";
import { Mic, Tv, ListMusic, Sparkles, UserCheck } from "lucide-react";
import { loginWithGoogle } from "../../lib/firebase";
import { AnonymousGuestModal } from "./AnonymousGuestModal";

export const LoginView: React.FC = () => {
  const [showAnonModal, setShowAnonModal] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    setError(null);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || "Failed to sign in with Google.");
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-8">
        {/* Logo & Headline */}
        <div className="space-y-3">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-2xl shadow-primary/30 animate-pulse">
            <Mic className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-wider font-heading text-text-main">
            SQUAD<span className="text-primary">-A-</span>ROKE
          </h1>
          <p className="text-sm text-text-muted max-w-sm mx-auto">
            The collaborative party karaoke system. Connect your TV, scan from your phone, and build the ultimate live queue with friends!
          </p>
        </div>

        {error && (
          <div className="p-3 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded-2xl">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 bg-card p-6 rounded-3xl border border-border shadow-xl">
          <button
            onClick={handleGoogleLogin}
            disabled={loadingGoogle}
            className="w-full py-3.5 px-6 rounded-2xl bg-white hover:bg-gray-100 text-gray-800 font-semibold text-sm flex items-center justify-center gap-3 transition-all shadow-md cursor-pointer disabled:opacity-60"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>{loadingGoogle ? "Signing in..." : "Continue with Google"}</span>
          </button>

          <div className="relative py-2 flex items-center justify-center">
            <div className="border-t border-border w-full" />
            <span className="bg-card px-3 text-[11px] text-text-muted uppercase tracking-wider font-semibold">
              Or
            </span>
          </div>

          <button
            onClick={() => setShowAnonModal(true)}
            className="w-full py-3.5 px-6 rounded-2xl bg-card border border-border hover:border-accent text-accent hover:text-accent-hover font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer"
          >
            <UserCheck className="w-4 h-4" />
            <span>Join as Guest (Instant Access)</span>
          </button>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 rounded-2xl bg-card/50 border border-border/50 flex flex-col items-center gap-1.5">
            <Tv className="w-5 h-5 text-primary" />
            <span className="text-[11px] font-semibold text-text-main">TV Display</span>
          </div>
          <div className="p-3 rounded-2xl bg-card/50 border border-border/50 flex flex-col items-center gap-1.5">
            <ListMusic className="w-5 h-5 text-accent" />
            <span className="text-[11px] font-semibold text-text-main">Live Queue</span>
          </div>
          <div className="p-3 rounded-2xl bg-card/50 border border-border/50 flex flex-col items-center gap-1.5">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="text-[11px] font-semibold text-text-main">Party Wrapped</span>
          </div>
        </div>
      </div>

      <AnonymousGuestModal
        isOpen={showAnonModal}
        onClose={() => setShowAnonModal(false)}
      />
    </div>
  );
};
